import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  TablePagination,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Search,
  Refresh,
  Download,
  Payment,
  TrendingUp,
  TrendingDown,
  AttachMoney,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getInvoicePayments } from '../redux/actions/invoiceActions';
import { getActivePaymentModes } from '../redux/actions/paymentModeActions';

const InvoicePaymentList = () => {
  const dispatch = useDispatch();
  
  // Redux state
  const { invoicePayments, loading, error } = useSelector(state => state.invoices || {});
  const { activePaymentModes } = useSelector(state => state.paymentModes || {});
  
  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    paymentModeId: '',
    startDate: null,
    endDate: null,
    page: 1,
    limit: 10,
  });

  // Load data on component mount
  useEffect(() => {
    console.log('🔄 Component mounted, loading data...');
    dispatch(getActivePaymentModes());
    loadPayments();
  }, []);

  // Load payments when filters change
  useEffect(() => {
    loadPayments();
  }, [filters.page, filters.limit, filters.paymentModeId, filters.startDate, filters.endDate]);

  const loadPayments = () => {
    console.log('🔄 Loading payments with filters:', filters);
    
    // Find the payment mode code for the selected payment mode ID
    const selectedPaymentMode = activePaymentModes.find(mode => mode.id === parseInt(filters.paymentModeId));
    
    const filterData = {
      ...filters,
      paymentModeId: selectedPaymentMode?.code || filters.paymentModeId, // Send code instead of ID
      startDate: filters.startDate ? filters.startDate.toISOString().split('T')[0] : null,
      endDate: filters.endDate ? filters.endDate.toISOString().split('T')[0] : null,
    };
    dispatch(getInvoicePayments(filterData));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filters change
    }));
    
    // If it's a payment mode filter, reload data
    if (field === 'paymentModeId') {
      loadPayments();
    }
  };

  const handleSearch = () => {
    loadPayments();
  };

  // Auto-search when search term changes (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        loadPayments();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleClearFilters = () => {
    setFilters({
      paymentModeId: '',
      startDate: null,
      endDate: null,
      page: 1,
      limit: 10,
    });
    setSearchTerm('');
    // Reload data after clearing filters
    loadPayments();
  };

  const handlePageChange = (event, page) => {
    setFilters(prev => ({ ...prev, page: page + 1 })); // TablePagination uses 0-based indexing
  };

  const handleRowsPerPageChange = (event) => {
    setFilters(prev => ({ 
      ...prev, 
      limit: parseInt(event.target.value, 10),
      page: 1 
    }));
  };

  // Filter payments based on search term (payment mode filtering is handled by API)
  const filteredPayments = (invoicePayments?.payments || []).filter(payment => {
    // Search term filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        payment.invoice?.invoice_no?.toLowerCase().includes(searchLower) ||
        payment.invoice?.customer?.name?.toLowerCase().includes(searchLower) ||
        payment.payment_mode?.name?.toLowerCase().includes(searchLower)
      );
    }
    
    return true;
  });

  // Calculate totals
  const totalAmount = (invoicePayments?.paymentModeTotals || []).reduce(
    (sum, total) => sum + parseFloat(total.total_amount || 0), 
    0
  );

  const totalPayments = (invoicePayments?.payments || []).length;

  // Show loading state
  if (loading && (!invoicePayments?.payments || invoicePayments.payments.length === 0)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading invoice payments...</Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error: {error}
        </Alert>
        <Button variant="contained" onClick={loadPayments}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Invoice Payments
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage invoice payments by payment mode
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AttachMoney sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h6">
                      ₹{totalAmount.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Payment sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h6">
                      {totalPayments}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Payments
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUp sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h6">
                      {invoicePayments?.paymentModeTotals?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Payment Modes
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingDown sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h6">
                      {filters.page} / {invoicePayments?.pagination?.totalPages || 1}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Current Page
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Payment Mode Summary */}
        {invoicePayments?.paymentModeTotals && invoicePayments.paymentModeTotals.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Mode Summary
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {invoicePayments.paymentModeTotals.map((total) => (
                  <Chip
                    key={total.payment_mode_code}
                    label={`${total.payment_mode?.name}: ₹${parseFloat(total.total_amount).toLocaleString()} (${total.payment_count})`}
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Payment Mode</InputLabel>
                  <Select
                    value={filters.paymentModeId}
                    onChange={(e) => handleFilterChange('paymentModeId', e.target.value)}
                    label="Payment Mode"
                  >
                    <MenuItem value="">All Payment Modes</MenuItem>
                    {activePaymentModes.map((mode) => (
                      <MenuItem key={mode.id} value={mode.id}>
                        {mode.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" onClick={handleSearch}>
                    Search
                  </Button>
                  <Button variant="outlined" onClick={handleClearFilters}>
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Payment Details ({filteredPayments.length} payments)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={loadPayments} size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export">
              <IconButton size="small">
                <Download />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Payments Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice No</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Payment Mode</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {payment.invoice?.invoice_no || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(payment.created_at).toLocaleDateString('en-GB')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {new Date(payment.created_at).toLocaleTimeString('en-GB', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payment.invoice?.customer?.name || 'Walk-in Customer'}
                    </Typography>
                    {payment.invoice?.customer?.phone && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {payment.invoice.customer.phone}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payment.payment_mode?.name || 'Unknown'}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      ₹{parseFloat(payment.amount).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payment.reference_number || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payment.status || 'active'}
                      color={payment.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* No Data Message */}
        {filteredPayments.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No payments found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters or search criteria
            </Typography>
          </Box>
        )}

        {/* Table Pagination */}
        {invoicePayments?.pagination && (
          <TablePagination
            component="div"
            count={invoicePayments.pagination.totalItems || 0}
            page={filters.page - 1} // Convert to 0-based index
            onPageChange={handlePageChange}
            rowsPerPage={filters.limit}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
          />
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default InvoicePaymentList;